import type { GameState, GameAction } from '../game/types';
import { speak } from '../services/speech';
import { Icons } from './Icons';
import { Card } from './Card';
import { PlayerPanel } from './PlayerPanel';
import { TurnTransition } from './TurnTransition';
import { ActionModals } from './ActionModals';
import { WinScreen } from './WinScreen';
import { ConfirmModal } from './ConfirmModal';
import { PAIRS_PER_DECK } from '../game/types';

type Props = {
  state: GameState;
  dispatch: React.Dispatch<GameAction>;
  onPlaySfx: (type: 'flip' | 'steal') => void;
  onToggleFullscreen: () => void;
};

export function GameBoard({ state, dispatch, onPlaySfx, onToggleFullscreen }: Props) {
  const currentPlayer = state.players[state.turn];
  const handleQuit = () => {
    dispatch({
      type: 'OPEN_CONFIRM',
      message: '確定要結束這場遊戲嗎？',
      onConfirm: () => {
        dispatch({ type: 'GO_TO_INTRO' });
      },
    });
  };

  const handleCardClick = (idx: number) => {
    if (state.isProcessing || state.modalType !== null) return;
    const card = state.cards[idx];
    if (!card || card.isFlipped || card.isMatched) return;
    onPlaySfx('flip');
    if (state.ttsEnabled) speak(card.content);
    dispatch({ type: 'FLIP_CARD', index: idx });
  };

  return (
    <div className="h-dvh flex flex-col items-center p-[clamp(0.5rem,2vh,1rem)] max-w-5xl mx-auto animate-fade-in relative overflow-hidden">
      <TurnTransition
        playerName={currentPlayer?.name ?? '未知農夫'}
        visible={state.showTurnLayer}
      />

      <div className="game-header shrink-0 w-full flex justify-between items-center bg-white/90 backdrop-blur rounded-2xl shadow-sm mb-[clamp(0.5rem,1.5vh,1.5rem)] border-b-4 border-green-200 p-[clamp(0.5rem,1.5vh,1rem)] z-20">
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={handleQuit}
            className="bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-500 p-2 rounded-lg transition shadow-sm"
            title="回到首頁"
          >
            <Icons.Home />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-gray-500 font-bold hidden sm:inline">回合：</span>
            <span className="text-[clamp(1rem,3vh,1.5rem)] font-black text-green-600">
              {currentPlayer?.name ?? ''}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onToggleFullscreen}
            className="p-[clamp(0.25rem,1vh,0.5rem)] rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300"
            title="全螢幕"
          >
            <Icons.Maximize />
          </button>
          <button
            onClick={() => dispatch({ type: 'TOGGLE_TTS' })}
            className={`p-[clamp(0.25rem,1vh,0.5rem)] rounded-full ${
              state.ttsEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-400'
            }`}
            title="語音朗讀"
          >
            {state.ttsEnabled ? <Icons.Mic /> : <Icons.MicOff />}
          </button>
          <button
            onClick={() => dispatch({ type: 'TOGGLE_BGM' })}
            className={`p-[clamp(0.25rem,1vh,0.5rem)] rounded-full ${
              state.bgmEnabled ? 'bg-purple-100 text-purple-600' : 'bg-gray-200 text-gray-400'
            }`}
            title="背景音樂"
          >
            {state.bgmEnabled ? <Icons.Music /> : <Icons.MusicOff />}
          </button>
          <button
            onClick={() => dispatch({ type: 'TOGGLE_SOUND' })}
            className={`p-[clamp(0.25rem,1vh,0.5rem)] rounded-full ${
              state.soundEnabled ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-400'
            }`}
            title="音效"
          >
            {state.soundEnabled ? <Icons.Volume /> : <Icons.VolumeX />}
          </button>
          <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full font-bold shadow-inner text-sm sm:text-base">
            配對：{PAIRS_PER_DECK - state.matchedCount}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 w-full flex items-center justify-center gap-[clamp(0.5rem,2vh,1.5rem)]">
        <PlayerPanel
          players={state.players}
          turn={state.turn}
          lastVictimId={state.lastVictimId}
          side="left"
        />
        <div className="card-grid grid grid-cols-4 gap-[clamp(0.25rem,1vh,1rem)] aspect-square h-full max-w-full max-h-full">
          {state.cards.map((card, idx) => (
            <Card key={card.id} card={card} onClick={() => handleCardClick(idx)} />
          ))}
        </div>
        <PlayerPanel
          players={state.players}
          turn={state.turn}
          lastVictimId={state.lastVictimId}
          side="right"
        />
      </div>

      <ActionModals state={state} dispatch={dispatch} onPlaySfx={onPlaySfx} />
      <WinScreen state={state} dispatch={dispatch} />
      <ConfirmModal
        open={state.confirmModal.open}
        message={state.confirmModal.message}
        onCancel={() => dispatch({ type: 'CLOSE_CONFIRM' })}
        onConfirm={() => state.confirmModal.onConfirm?.()}
      />
    </div>
  );
}
